import equal from "fast-deep-equal/es6";
import { ApiBulkRoutePatch, RouteDifficulty } from "griptonite-api-client";
import z from "zod";

import { Route } from "../../core/types";

export type RouteFormValues = {
  circuit: {
    uuid: string | null;
    number: number | null;
    difficulty: RouteDifficulty | null;
  } | null;
  gradeValue: string | null;
  gradeSystem: string | null;
  name: string | null;
  description: string | null;
  zone: string | null;
  styles: string[];
  skills: string[];
  holds: string[];
  setters: string[];
  colors: string | null;
  scheduledEndTime: string | null;
  startLine: string | null;
  quickdrawCount: number | null;
  height: number | null;
  autoBelay: boolean | null;
  climbType: "BOULDER" | "SPORT";
  map: string | null;
  mapPosition: { x: number; y: number } | null;
  gradeEstimated: boolean;
  tag: string | null;
  competition: string | null;
  competitionRouteGroups: string[];
  segmentsCount: number | null;
  labels: string[];
};

export type RouteBulkFormValues = {
  circuit: string | null;
  gradeValue: string | null;
  gradeSystem: string | null;
  zone: string | null;
  styles: string[];
  skills: string[];
  holds: string[];
  setters: string[];
  colors: string | null;
  scheduledEndTime: string | null;
  startLine: string | null;
  quickdrawCount: number | null;
  height: number | null;
  autoBelay: boolean | null;
  climbType: "BOULDER" | "SPORT";
  competition: string | null;
  competitionRouteGroups: string[];
  _fieldsWithMultipleValues: string[];
  segmentsCount: number | null;
};

const emptyArray = [] as never[];

export function routeToFormValues(route?: Partial<Route>): RouteFormValues {
  return {
    name: route?.name || null,
    description: route?.description || null,
    gradeValue: route?.gradeValue || null,
    gradeSystem: route?.gradeSystem || null,
    zone: route?.zone?.uuid ?? null,
    styles: route?.styles || emptyArray,
    skills: route?.skills || emptyArray,
    holds: route?.holds || emptyArray,
    circuit: {
      uuid: route?.circuit?.uuid || null,
      number: route?.circuit?.number || route?.circuitNumber || null,
      difficulty: route?.circuit?.difficulty || null,
    },
    setters: route?.setters?.map((setter) => setter.uuid) || emptyArray,
    colors: route?.colors || null,
    scheduledEndTime: route?.scheduledEndTime || null,
    startLine: route?.startLine?.uuid || null,
    quickdrawCount: route?.quickdrawCount || null,
    height: route?.height || null,
    autoBelay: route?.autoBelay ?? false,
    climbType: route?.climbType || "BOULDER",
    map: route?.map || null,
    mapPosition: route?.mapPosition || null,
    gradeEstimated: route?.gradeEstimated || false,
    tag: route?.tag?.id || null,
    competition: route?.competitionRouteGroups?.[0]?.competition || null,
    competitionRouteGroups: route?.competitionRouteGroups?.map((group) => group.uuid) || emptyArray,
    segmentsCount: route?.segmentsCount || null,
    labels: route?.labels?.map((label) => label.uuid) || emptyArray,
  };
}

const singleDistinctOrEmpty = <T>(
  arr: T[],
):
  | { multipleValues: true; value: T extends unknown[] ? never[] : null }
  | { multipleValues: false; value: T } => {
  // If all the values are the same via deep equality, return that value.
  // Otherwise, return null.
  const firstValue = arr[0];
  if (arr.every((value) => equal(value, firstValue))) {
    return { multipleValues: false, value: firstValue } as {
      multipleValues: false;
      value: T;
    };
  }
  return {
    multipleValues: true,
    value: Array.isArray(firstValue) ? [] : null,
  } as { multipleValues: true; value: T extends unknown[] ? never[] : null };
};

const bulkRouteAccessors = {
  circuit: (route: Route) => route.circuit?.uuid || null,
  gradeValue: (route: Route) => route.gradeValue,
  gradeSystem: (route: Route) => route.gradeSystem,
  zone: (route: Route) => route.zone?.uuid || null,
  styles: (route: Route) => route.styles,
  skills: (route: Route) => route.skills,
  holds: (route: Route) => route.holds,
  setters: (route: Route) => route.setters.map((setter) => setter.uuid),
  colors: (route: Route) => route.colors,
  scheduledEndTime: (route: Route) => route.scheduledEndTime,
  startLine: (route: Route) => route.startLine?.uuid || null,
  quickdrawCount: (route: Route) => route.quickdrawCount,
  height: (route: Route) => route.height,
  autoBelay: (route: Route) => route.autoBelay,
  climbType: (route: Route) => route.climbType,
  competition: (route: Route) => route.competitionRouteGroups?.[0]?.competition,
  competitionRouteGroups: (route: Route) => route.competitionRouteGroups.map((group) => group.uuid),
} as const;

export function routesToBulkFormValues(routes: Route[]): RouteBulkFormValues {
  const result = {
    _fieldsWithMultipleValues: [] as string[],
  } as RouteBulkFormValues;
  Object.entries(bulkRouteAccessors).forEach(([key, accessor]) => {
    const values = routes.map((route) => accessor(route));
    const { multipleValues, value } = singleDistinctOrEmpty(values);
    if (multipleValues) {
      result._fieldsWithMultipleValues.push(key);
    }
    // Each accessor returns the type of its own field, which TypeScript
    // can't match up with the key here.
    (result as Record<string, unknown>)[key] = value;
  });
  return result;
}

export function bulkFormValuesToRoutePatches(
  formValues: Partial<RouteBulkFormValues>,
  routeUuids: string[],
): ApiBulkRoutePatch[] {
  const patches = routeUuids.map((routeUuid) => {
    const { _fieldsWithMultipleValues, ...values } = formValues;
    const patch: ApiBulkRoutePatch = { ...values, uuid: routeUuid };
    return patch;
  });
  return patches;
}

export const RouteFormValuesSchema = z.object({
  name: z.string().trim().nullable(),
  description: z.string().trim().nullable(),
  gradeValue: z.string().nullable(),
  gradeSystem: z.string().nullable(),
  zone: z.string().uuid().nullable(),
  styles: z.array(z.string()),
  skills: z.array(z.string()),
  holds: z.array(z.string()),
  circuit: z
    .object({
      uuid: z.string().uuid().nullable(),
      number: z.number().int().positive().nullable(),
      difficulty: z.union([z.literal(-1), z.literal(0), z.literal(1), z.literal(2)]).nullable(),
    })
    .nullable(),
  setters: z.array(z.string().uuid()),
  colors: z
    .string()
    .regex(
      // list of 6-digit hex colors separated by :
      /^#[0-9a-fA-F]{6}(:#[0-9a-fA-F]{6})*$/,
    )
    .nullable(),
  scheduledEndTime: z.string().datetime().nullable(),
  startLine: z.string().uuid().nullable(),
  quickdrawCount: z.number().int().nonnegative().max(250, "Max quickdraw count is 250").nullable(),
  height: z.number().positive().max(10000, "Max height 100m/328ft").nullable(),
  autoBelay: z.boolean().nullable(),
  climbType: z.union([z.literal("BOULDER"), z.literal("SPORT")]),
  map: z.string().uuid().nullable(),
  mapPosition: z
    .object({
      x: z.number(),
      y: z.number(),
    })
    .nullable(),
  gradeEstimated: z.boolean(),
  tag: z.string().nullable(),
  competition: z.string().uuid().nullable(),
  competitionRouteGroups: z.array(z.string().uuid()),
  segmentsCount: z.number().int().nonnegative().nullable(),
  labels: z.array(z.string().uuid()),
});

export const PublishRouteFormValuesSchema = RouteFormValuesSchema.superRefine((obj, ctx) => {
  if (!obj.competition) {
    if (!obj.gradeValue) {
      ctx.addIssue({
        message: "Grade is required",
        code: z.ZodIssueCode.custom,
        path: ["gradeValue"],
      });
    }
    if (!obj.gradeSystem) {
      ctx.addIssue({
        message: "Grade system is required",
        code: z.ZodIssueCode.custom,
        path: ["gradeSystem"],
      });
    }
  }
  if (obj.climbType === "BOULDER") {
    if (!obj.circuit?.uuid && !obj.competition) {
      ctx.addIssue({
        message: "Circuit is required",
        code: z.ZodIssueCode.custom,
        path: ["circuit.uuid"],
      });
    }
    if (!obj.mapPosition) {
      ctx.addIssue({
        message: "Map position is required",
        code: z.ZodIssueCode.custom,
        path: ["mapPosition"],
      });
    }
    if (!obj.map) {
      ctx.addIssue({
        message: "Map is required",
        code: z.ZodIssueCode.custom,
        path: ["map"],
      });
    }
  } else if (obj.climbType === "SPORT") {
    if (!obj.startLine && !obj.competition) {
      ctx.addIssue({
        message: "Start line is required",
        code: z.ZodIssueCode.custom,
        path: ["startLine"],
      });
    }
    if (!obj.startLine && !obj.mapPosition) {
      ctx.addIssue({
        message: "Map position is required",
        code: z.ZodIssueCode.custom,
        path: ["mapPosition"],
      });
    }
    if (!obj.startLine && !obj.map) {
      ctx.addIssue({
        message: "Map is required",
        code: z.ZodIssueCode.custom,
        path: ["map"],
      });
    }
    if (!obj.startLine && !obj.quickdrawCount) {
      ctx.addIssue({
        message: "Quickdraw count is required",
        code: z.ZodIssueCode.custom,
        path: ["quickdrawCount"],
      });
    }
    if (!obj.startLine && !obj.height) {
      ctx.addIssue({
        message: "Height is required",
        code: z.ZodIssueCode.custom,
        path: ["height"],
      });
    }
    if (obj.competition && !obj.segmentsCount) {
      ctx.addIssue({
        message: "Hold count is required",
        code: z.ZodIssueCode.custom,
        path: ["segmentsCount"],
      });
    }
  }
});
